import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  makeStyles,
} from "@material-ui/core";
import SchedulePicker from "./SchedulePicker";
import { ScrapeConfig, UpdateScrapeConfig } from "../../Types/Scrapes";

const useStyles = makeStyles(() => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    paddingTop: "8px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  deleteButton: {
    color: "#f44336",
    borderColor: "#f44336",
    marginRight: "auto",
  },
}));

interface EditScrapeModalProps {
  open: boolean;
  onClose: () => void;
  config: ScrapeConfig;
  onSave: (data: UpdateScrapeConfig) => void;
  onDelete: () => void;
  isSaving: boolean;
}

export default function EditScrapeModal({
  open,
  onClose,
  config,
  onSave,
  onDelete,
  isSaving,
}: EditScrapeModalProps) {
  const classes = useStyles();
  const [scrapeName, setScrapeName] = useState(config.scrapeName || "");
  const [url, setUrl] = useState(config.url);
  const [cronExpression, setCronExpression] = useState(config.cronExpression);
  const [isActive, setIsActive] = useState(config.isActive);

  useEffect(() => {
    if (open) {
      setScrapeName(config.scrapeName || "");
      setUrl(config.url);
      setCronExpression(config.cronExpression);
      setIsActive(config.isActive);
    }
  }, [open, config]);

  const hasChanges =
    scrapeName !== (config.scrapeName || "") ||
    url !== config.url ||
    cronExpression !== config.cronExpression ||
    isActive !== config.isActive;

  const handleSave = () => {
    const update: UpdateScrapeConfig = {};
    if (scrapeName !== (config.scrapeName || "")) update.scrapeName = scrapeName;
    if (url !== config.url) update.url = url;
    if (cronExpression !== config.cronExpression) update.cronExpression = cronExpression;
    if (isActive !== config.isActive) update.isActive = isActive;
    onSave(update);
  };

  const handleClose = () => {
    setScrapeName(config.scrapeName || "");
    setUrl(config.url);
    setCronExpression(config.cronExpression);
    setIsActive(config.isActive);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Configuration</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <TextField
            label="Scrape Name"
            value={scrapeName}
            onChange={(e) => setScrapeName(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Optional"
          />
          <TextField
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
          <SchedulePicker value={cronExpression} onChange={setCronExpression} />
          <FormControlLabel
            control={
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
            }
            label={isActive ? "Active" : "Inactive"}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" size="small" className={classes.deleteButton} onClick={onDelete}>
          Delete
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
